import * as Sentry from '@sentry/nextjs';
import {
  CurveResult,
  evaluateAt,
  StrategyKernel,
  StrategyPoint,
  sweepCurve,
} from 'utils/shared/quantlib/strategyKernel';
import {
  OpcCurveRequest,
  OpcPointRequest,
  OpcTableRequest,
  PreparedOpcPricing,
  prepareTotalFinanceOpcPricing,
  snapshotOpcPricingKernel,
  validateOpcPointRequest,
  validateOpcCurveRequest,
  validateOpcTableRequest,
} from 'utils/shared/totalfinance/opcAdapter';
import { calculateReturnOnCost } from 'utils/shared/quantlib/returnOnCost';
import {
  INTRINSIC_LIMIT_TIME_YEARS,
  OPTIONS_PER_CONTRACT,
  YEAR_IN_MS,
} from 'utils/shared/quantlib/utils';
import { CalculationEngineMode, OPC_CALCULATION_ENGINE } from 'constants/flags';
import { resolveCalculationEngineMode } from 'utils/shared/totalfinance/engineMode';

export type OpcPricingMode = CalculationEngineMode;
export type OpcPricingEngineName = 'legacy' | 'totalfinance';

/**
 * Code default from `constants/flags`, overridden per environment by the build-time
 * NEXT_PUBLIC_TOTALFINANCE_OPC_MODE. An unrecognised override rolls back to legacy.
 */
export function resolveOpcPricingMode(
  value: string | undefined,
  codeDefault: CalculationEngineMode = OPC_CALCULATION_ENGINE,
): OpcPricingMode {
  return resolveCalculationEngineMode(value, codeDefault);
}

export function getOpcPricingMode(): OpcPricingMode {
  return resolveOpcPricingMode(process.env.NEXT_PUBLIC_TOTALFINANCE_OPC_MODE);
}

export interface OpcPricingComparison {
  operation: 'point' | 'curve' | 'table';
  mode: Exclude<OpcPricingMode, 'legacy'>;
  selectedEngine: OpcPricingEngineName;
  status: 'match' | 'mismatch' | 'unavailable';
  pointCount: number;
  optionLegCount: number;
  maxProfitDifference: number | null;
  /** Per-Greek errors retain their distinct units; never combine delta and dollar sensitivities. */
  maxGreekDifferences: Record<'delta' | 'gamma' | 'theta' | 'vega' | 'rho', number> | null;
  breakEvensMatch: boolean | null;
  /** Bounded, non-sensitive reason codes only; no raw position/quote payload is published. */
  reasons: readonly string[];
}

const MAX_DIAGNOSTICS = 20;
const recentComparisons: OpcPricingComparison[] = [];

/** Bounded, browser-only diagnostic history of the latest comparisons. */
export function getOpcPricingDiagnostics(): readonly OpcPricingComparison[] {
  return recentComparisons.slice();
}

export function clearOpcPricingDiagnostics(): void {
  recentComparisons.length = 0;
}

const reportedDisagreements = new Set<string>();

/**
 * A disagreement or TotalFinance failure is what rollout confidence rests on, so it is reported to
 * Sentry as a warning: once per page load per kind, with the surface, mode, status, bounded reason
 * codes and leg count. The report adds no price, strike, quote or position; Sentry attaches its
 * standard event context (page URL, browser, release) as it does to every app event.
 */
function reportDisagreement(comparison: OpcPricingComparison): void {
  const key = [
    comparison.mode,
    comparison.operation,
    comparison.status,
    ...comparison.reasons,
  ].join(':');
  if (reportedDisagreements.has(key)) return;
  reportedDisagreements.add(key);
  Sentry.withScope((scope) => {
    scope.setLevel(Sentry.Severity.Warning);
    scope.setTag('totalfinance_surface', comparison.operation);
    scope.setTag('totalfinance_mode', comparison.mode);
    scope.setTag('totalfinance_status', comparison.status);
    scope.setTag('totalfinance_selected', comparison.selectedEngine);
    scope.setContext('totalfinance_opc', {
      reasons: [...comparison.reasons],
      optionLegCount: comparison.optionLegCount,
    });
    Sentry.captureMessage('TotalFinance OPC pricing disagreed with legacy');
  });
}

/** Test seam: forget which disagreements this page load already reported. */
export function resetOpcDisagreementReports(): void {
  reportedDisagreements.clear();
}

function recordComparison(comparison: OpcPricingComparison): void {
  // Don't keep cross-user calculation history in a long-lived SSR process.
  if (typeof window === 'undefined') return;
  recentComparisons.push(
    Object.freeze({ ...comparison, reasons: Object.freeze([...comparison.reasons]) }),
  );
  if (recentComparisons.length > MAX_DIAGNOSTICS) recentComparisons.shift();
  if (comparison.status !== 'match') reportDisagreement(comparison);
}

export interface OpcPricingSession {
  readonly mode: OpcPricingMode;
  value(input: OpcPointRequest): StrategyPoint;
  curve(input: OpcCurveRequest): CurveResult;
  table(input: OpcTableRequest): StrategyPoint[][];
  /** Most recent comparison for this calculation session, including automatic rollback. */
  latestComparison(): OpcPricingComparison | null;
  /** Latest result per surface: a matching point cannot hide a table/curve rollback. */
  comparisons(): readonly OpcPricingComparison[];
}

const POINT_FIELDS = [
  'stockPrice',
  'contractValue',
  'profit',
  'profitPerContract',
  'delta',
  'gamma',
  'theta',
  'vega',
  'rho',
] as const;

/**
 * Per-contract agreement floors, each in its own unit. They scale with the ABSOLUTE number of
 * option contracts (a 10-lot has ten contracts' worth of rounding), never with net size.
 */
export const OPC_COMPARISON_TOLERANCES = Object.freeze({
  /** Dollars of position P&L per contract. */
  profit: 0.01,
  /** Dollars per share, per contract (contract value and P&L per contract). */
  valuePerShare: 0.0001,
  /** Position delta in shares, per contract. */
  delta: 0.0001,
  /** Position gamma in shares per $1, per contract. */
  gamma: 0.00001,
  /** Position theta in dollars per calendar day, per contract. */
  theta: 0.0001,
  /** Position vega in dollars per volatility point, per contract. */
  vega: 0.0001,
  /** Position rho in dollars per rate point, per contract. */
  rho: 0.0001,
  /** Dollars of underlying price between matching break-evens (two display cents). */
  breakEven: 0.02,
});

/**
 * The legacy kernel's normal CDF is Abramowitz & Stegun 7.1.26: |erf error| ≤ 1.5e-7, so
 * |Φ error| ≤ 7.5e-8. TotalFinance uses West (2009), accurate to ~1e-15, and agrees with a
 * 40-digit reference (see opcOracle.test.ts). The legacy reference therefore carries a known,
 * price-proportional error — about $0.011 per contract on a $750 underlying and $0.10 on a $6,500
 * one — that a fixed dollar floor would misreport as a TotalFinance defect.
 */
export const LEGACY_NORMAL_CDF_ERROR_BOUND = 7.5e-8;

interface MetricAllowance {
  valuePerShare: number;
  profit: number;
  delta: number;
  theta: number;
  rho: number;
}

/**
 * Worst-case legacy approximation error at one spot and instant, derived term by term from the
 * legacy formulas (strategyKernel `evalLeg`): premium uses Φ(±d1)·S and Φ(±d2)·K·e^(−rT), delta
 * uses Φ(d1), theta uses r·K·e^(−rT)·Φ(±d2)/365 and rho uses K·T·e^(−rT)·Φ(±d2)/100. Gamma and
 * vega use only the exact normal density, so they receive no allowance. Settled legs (inside the
 * intrinsic cutoff) and the S = 0 analytic limit involve no Φ and contribute nothing.
 */
export function legacyApproximationAllowance({
  kernel,
  spot,
  asOf,
}: {
  kernel: StrategyKernel;
  spot: number;
  asOf: number;
}): MetricAllowance {
  const allowance = { valuePerShare: 0, profit: 0, delta: 0, theta: 0, rho: 0 };
  if (!(spot > 0)) return allowance;
  for (const leg of kernel.legs) {
    const time = (leg.expiryMs - asOf) / YEAR_IN_MS;
    if (leg.signedContracts === 0 || time <= INTRINSIC_LIMIT_TIME_YEARS) continue;
    const contracts = Math.abs(leg.signedContracts);
    const discountedStrike = leg.strike * Math.exp(-leg.riskFreeRate * time);
    const perShare = LEGACY_NORMAL_CDF_ERROR_BOUND * (spot + discountedStrike);
    allowance.valuePerShare += contracts * perShare;
    allowance.profit += contracts * perShare * OPTIONS_PER_CONTRACT;
    allowance.delta += contracts * OPTIONS_PER_CONTRACT * LEGACY_NORMAL_CDF_ERROR_BOUND;
    allowance.theta +=
      (contracts *
        OPTIONS_PER_CONTRACT *
        Math.abs(leg.riskFreeRate) *
        discountedStrike *
        LEGACY_NORMAL_CDF_ERROR_BOUND) /
      365;
    allowance.rho += contracts * discountedStrike * time * LEGACY_NORMAL_CDF_ERROR_BOUND;
  }
  return allowance;
}

/**
 * Shared by tests and all UI paths. Each point is judged at its own spot and valuation instant:
 * floor × absolute contracts, plus the legacy approximation allowance for that point. A candidate
 * outside that band is a mismatch; nothing is widened to hide a real disagreement.
 */
export function compareOpcPoints({
  kernel,
  baseline,
  candidate,
  asOf,
}: {
  kernel: StrategyKernel;
  baseline: readonly StrategyPoint[];
  candidate: readonly StrategyPoint[];
  /** One instant for every point (point/curve), or one per point (payoff table cells). */
  asOf: number | readonly number[];
}): Pick<
  OpcPricingComparison,
  'status' | 'maxProfitDifference' | 'maxGreekDifferences' | 'reasons'
> {
  const reasons = new Set<string>();
  let maxProfitDifference = 0;
  const maxGreekDifferences = { delta: 0, gamma: 0, theta: 0, vega: 0, rho: 0 };
  const contracts = kernel.legs.reduce((sum, leg) => sum + Math.abs(leg.signedContracts), 0);
  const netEntryCost =
    kernel.originalContractValue * OPTIONS_PER_CONTRACT + kernel.originalStockPositionValue;
  const scale = Math.max(1, contracts);
  const tolerance = OPC_COMPARISON_TOLERANCES;
  if (baseline.length !== candidate.length) reasons.add('point_count');
  if (typeof asOf !== 'number' && asOf.length !== baseline.length) reasons.add('point_count');
  for (let index = 0; index < Math.min(baseline.length, candidate.length); index++) {
    const left = baseline[index],
      right = candidate[index];
    if (
      POINT_FIELDS.some((field) => !Number.isFinite(left[field]) || !Number.isFinite(right[field]))
    ) {
      reasons.add('non_finite_result');
      continue;
    }
    if (left.stockPrice !== right.stockPrice) reasons.add('spot_grid');
    const allowance = legacyApproximationAllowance({
      kernel,
      spot: left.stockPrice,
      asOf: typeof asOf === 'number' ? asOf : asOf[index],
    });
    const profitDifference = Math.abs(left.profit - right.profit);
    maxProfitDifference = Math.max(maxProfitDifference, profitDifference);
    const valueBand = tolerance.valuePerShare * scale + allowance.valuePerShare;
    if (
      profitDifference > tolerance.profit * scale + allowance.profit ||
      Math.abs(left.contractValue - right.contractValue) > valueBand ||
      Math.abs(left.profitPerContract - right.profitPerContract) > valueBand
    )
      reasons.add('valuation');
    const greekBand = {
      delta: tolerance.delta * scale + allowance.delta,
      gamma: tolerance.gamma * scale,
      theta: tolerance.theta * scale + allowance.theta,
      vega: tolerance.vega * scale,
      rho: tolerance.rho * scale + allowance.rho,
    };
    for (const field of ['delta', 'gamma', 'theta', 'vega', 'rho'] as const) {
      const error = Math.abs(left[field] - right[field]);
      maxGreekDifferences[field] = Math.max(maxGreekDifferences[field], error);
      if (error > greekBand[field]) reasons.add(field);
    }
    if (
      (left.profitPercent === null) !== (right.profitPercent === null) ||
      left.profitPercentReason !== right.profitPercentReason
    )
      reasons.add('return_availability');
    if (
      (left.profitPercent !== null && !Number.isFinite(left.profitPercent)) ||
      (right.profitPercent !== null && !Number.isFinite(right.profitPercent))
    )
      reasons.add('non_finite_return');
    // The denominator is shared and immutable; compare the underlying dollar P&L rather than
    // penalizing a large percentage error amplified by a legitimately tiny entry debit.
    for (const point of [left, right]) {
      const expected = calculateReturnOnCost({ profit: point.profit, netEntryCost });
      if (
        point.profitPercentReason !== expected.profitPercentReason ||
        (expected.profitPercent === null
          ? point.profitPercent !== null
          : point.profitPercent === null ||
            Math.abs(point.profitPercent - expected.profitPercent) >
              8 * Number.EPSILON * Math.max(1, Math.abs(expected.profitPercent)))
      )
        reasons.add('return_consistency');
    }
  }
  return {
    status: reasons.size ? 'mismatch' : 'match',
    maxProfitDifference,
    maxGreekDifferences: Object.freeze(maxGreekDifferences),
    reasons: [...reasons],
  };
}

export class OpcPricingUnavailableError extends Error {
  constructor() {
    super(
      'The selected strategy cannot be priced with finite results. Check its inputs before retrying.',
    );
    this.name = 'OpcPricingUnavailableError';
  }
}

/**
 * Single point/curve/table selection seam. TotalFinance mode stays dual-run and guarded: a failed or
 * mismatched candidate never replaces the legacy calculation, and the disagreement is reported.
 */
export function createOpcPricingSession({
  kernel,
  mode = getOpcPricingMode(),
  onComparison = recordComparison,
  prepare = prepareTotalFinanceOpcPricing,
}: {
  kernel: StrategyKernel;
  mode?: OpcPricingMode;
  onComparison?: (comparison: OpcPricingComparison) => void;
  /** Injectable calculation dependency for failure/rollback tests, not a remote transport. */
  prepare?: typeof prepareTotalFinanceOpcPricing;
}): OpcPricingSession {
  // Both engines use the same immutable, prepared selected-leg snapshot, even if a caller mutates
  // the original constructor arguments after the session was created.
  const snapshot = snapshotOpcPricingKernel({ kernel });
  let candidate: PreparedOpcPricing | undefined;
  let last: OpcPricingComparison | null = null;
  const surfaces = new Map<OpcPricingComparison['operation'], OpcPricingComparison>();
  const run = <T>(
    operation: OpcPricingComparison['operation'],
    baseline: T,
    calculate: (pricing: PreparedOpcPricing) => T,
    points: (result: T) => StrategyPoint[],
    asOf: number | readonly number[],
    breakEvens?: (result: T) => number[],
  ): T => {
    if (
      points(baseline).some((point) => POINT_FIELDS.some((field) => !Number.isFinite(point[field])))
    ) {
      throw new OpcPricingUnavailableError();
    }
    if (mode === 'legacy') return baseline;
    let output: T | undefined;
    let comparison: OpcPricingComparison;
    try {
      candidate ??= prepare({ kernel: snapshot });
      output = calculate(candidate);
      const checked = compareOpcPoints({
        kernel: snapshot,
        baseline: points(baseline),
        candidate: points(output),
        asOf,
      });
      let breakEvensMatch: boolean | null = null;
      if (breakEvens) {
        const left = breakEvens(baseline),
          right = breakEvens(output);
        breakEvensMatch =
          left.length === right.length &&
          left.every(
            (price, index) =>
              // Break-evens are rounded to cents; allow for float representation of the band.
              Math.abs(price - right[index]) <= OPC_COMPARISON_TOLERANCES.breakEven + 1e-9,
          );
      }
      const matched = checked.status === 'match' && breakEvensMatch !== false;
      comparison = {
        ...checked,
        operation,
        mode,
        pointCount: points(baseline).length,
        optionLegCount: snapshot.legs.length,
        breakEvensMatch,
        status: matched ? 'match' : 'mismatch',
        selectedEngine: mode === 'totalfinance' && matched ? 'totalfinance' : 'legacy',
        reasons: breakEvensMatch === false ? [...checked.reasons, 'break_evens'] : checked.reasons,
      };
    } catch {
      comparison = {
        operation,
        mode,
        selectedEngine: 'legacy',
        status: 'unavailable',
        pointCount: points(baseline).length,
        optionLegCount: snapshot.legs.length,
        maxProfitDifference: null,
        maxGreekDifferences: null,
        breakEvensMatch: null,
        reasons: ['candidate_unavailable'],
      };
    }
    last = Object.freeze({ ...comparison, reasons: Object.freeze([...comparison.reasons]) });
    surfaces.set(operation, last);
    // Diagnostics are observational. A subscriber failure must not change the financial result.
    try {
      onComparison(last);
    } catch {
      /* caller-owned observer */
    }
    if (
      comparison.selectedEngine === 'legacy' &&
      points(baseline).some((point) => POINT_FIELDS.some((field) => !Number.isFinite(point[field])))
    ) {
      // A broken reference is not a safe rollback; make the unavailable calculation explicit.
      throw new OpcPricingUnavailableError();
    }
    return comparison.selectedEngine === 'totalfinance' ? output! : baseline;
  };
  return {
    mode,
    latestComparison: () => last,
    comparisons: () => [...surfaces.values()],
    value: (input) => {
      validateOpcPointRequest(input, snapshot);
      return run(
        'point',
        evaluateAt(snapshot, input.spot, input.asOf),
        (pricing) => pricing.value(input),
        (result) => [result],
        input.asOf,
      );
    },
    curve: (input) => {
      validateOpcCurveRequest(input, snapshot);
      return run(
        'curve',
        sweepCurve(
          snapshot,
          input.from,
          input.to,
          input.steps,
          input.asOf,
          input.injectStrikes ?? true,
        ),
        (pricing) => pricing.curve(input),
        (result) => result.payoff,
        input.asOf,
        (result) => result.breakEvens,
      );
    },
    table: (input) => {
      validateOpcTableRequest(input, snapshot);
      return run(
        'table',
        input.prices.map((spot) => input.times.map((asOf) => evaluateAt(snapshot, spot, asOf))),
        (pricing) => pricing.table(input),
        (result) => result.flat(),
        // Price outer, time inner: cell (i, j) is valued at times[j].
        input.prices.flatMap(() => input.times),
      );
    },
  };
}
